import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import AddCardRoundedIcon from '@mui/icons-material/AddCardRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';

import { PRIMARYCOLOR } from '../../Constants';
import { useCredits } from '../../context/CreditsContext';
import {
  clearCheckoutContext,
  cancelSubscription,
  createCreditPurchaseCheckout,
  createSubscriptionCheckout,
  createSubscriptionPortal,
  getCreditPricing,
  getCreditTransactions,
  previewSubscriptionChange,
  readCheckoutContext,
  removeScheduledSubscriptionChange,
  resumeSubscription,
  scheduleSubscriptionChange,
  saveCheckoutContext
} from '../../services/creditsApi';

const BRAND = PRIMARYCOLOR || '#005CC5';
const PAGE_SIZE = 50;
const surfaceSx = {
  borderRadius: 4,
  border: '1px solid',
  borderColor: alpha(BRAND, 0.12),
  boxShadow: `0 18px 42px ${alpha('#16345C', 0.07)}`,
  backgroundColor: '#ffffff'
};

const numberValue = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatNumber = (value) => new Intl.NumberFormat().format(numberValue(value));

const formatCurrency = (amount, currency = 'usd') => {
  if (amount === null || amount === undefined || amount === '') return 'Pricing unavailable';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: String(currency || 'usd').toUpperCase()
    }).format(numberValue(amount) / 100);
  } catch {
    return `${numberValue(amount) / 100} ${String(currency || 'USD').toUpperCase()}`;
  }
};

const formatDate = (value, includeTime = false) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(includeTime ? { hour: 'numeric', minute: '2-digit' } : {})
  }).format(date);
};

const planInterval = (plan) => {
  const interval = String(plan?.interval || 'month').toLowerCase();
  const count = numberValue(plan?.interval_count, 1);
  if (interval === 'year') return count === 1 ? 'year' : `${count} years`;
  return count === 1 ? 'month' : `${count} months`;
};

const friendlyPlanName = (plan) => {
  if (!plan) return 'Selected plan';
  const rawName = String(plan?.name || '').trim();
  const cleanedName = rawName
    .replace(/stealthnet/gi, '')
    .replace(/\bai\b/gi, '')
    .replace(/\b(monthly|annual|yearly)\b/gi, '')
    .replace(/\(\s*\d+\s*\)/g, '')
    .replace(/\bplan\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (cleanedName) return cleanedName;
  const cadence = String(plan?.interval || 'month').toLowerCase() === 'year' ? 'annual' : 'monthly';
  return `${formatNumber(plan?.credits)}-credit ${cadence} plan`;
};

const SectionTitle = ({ eyebrow, title, description, action }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5}>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ color: BRAND, fontSize: '0.7rem', fontWeight: 900, letterSpacing: '0.09em', textTransform: 'uppercase' }}>{eyebrow}</Typography>
      <Typography variant="h5" sx={{ mt: 0.35, fontWeight: 900, color: '#0B203A', overflowWrap: 'anywhere' }}>{title}</Typography>
      {description ? <Typography variant="body2" sx={{ mt: 0.45, color: '#65758B', lineHeight: 1.65, maxWidth: 760 }}>{description}</Typography> : null}
    </Box>
    {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
  </Stack>
);

const subscriptionStatusColor = (status) => {
  const value = String(status || '').toLowerCase();
  if (['active', 'trialing'].includes(value)) return 'success';
  if (['past_due', 'unpaid', 'incomplete'].includes(value)) return 'warning';
  if (['canceled', 'cancelled'].includes(value)) return 'default';
  return 'info';
};

const transactionAmount = (transaction) => numberValue(
  transaction?.amount ?? transaction?.credit_amount ?? transaction?.credits ?? transaction?.delta,
  0
);

const billingActionErrorMessage = (error, fallback) => {
  const message = error?.message || fallback;
  if (/only team owners and admins/i.test(message)) {
    return 'Only team owners and administrators can make billing changes. Ask a team owner or administrator to complete this action.';
  }
  return message;
};

const checkoutUpdated = (snapshot, nextBalance, nextPricing) => {
  const subscription = nextPricing?.current_subscription || null;
  if (!snapshot) {
    return ['active', 'trialing'].includes(String(subscription?.status || '').toLowerCase());
  }

  if (snapshot.kind === 'subscription') {
    return (
      subscription?.price_id === snapshot.selection_id &&
      ['active', 'trialing'].includes(String(subscription?.status || '').toLowerCase())
    );
  }

  if (snapshot.kind === 'purchase') {
    return (
      numberValue(nextBalance?.available_credits) > numberValue(snapshot.available_credits) ||
      numberValue(nextBalance?.purchased_credits) > numberValue(snapshot.purchased_credits)
    );
  }

  return false;
};

const Metric = ({ label, value, helper, accent = BRAND }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.5,
      height: '100%',
      borderRadius: 3,
      border: '1px solid',
      borderColor: alpha(accent, 0.14),
      bgcolor: alpha(accent, 0.035)
    }}
  >
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: accent }}>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ mt: 0.45, fontWeight: 900, color: '#10243E', overflowWrap: 'anywhere' }}>{value}</Typography>
    {helper ? <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: '#65758B', lineHeight: 1.45 }}>{helper}</Typography> : null}
  </Paper>
);

const EmptyState = ({ title, description }) => (
  <Box sx={{ py: 5, px: 2, textAlign: 'center' }}>
    <SavingsRoundedIcon sx={{ fontSize: 42, color: alpha(BRAND, 0.45) }} />
    <Typography sx={{ mt: 1, fontWeight: 850, color: '#10243E' }}>{title}</Typography>
    <Typography variant="body2" sx={{ mt: 0.5, color: '#65758B' }}>{description}</Typography>
  </Box>
);

const BillingPage = () => {
  const { balance, loading: balanceLoading, error: balanceError, refreshBalance } = useCredits();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnStatus = searchParams.get('credits');
  const [pricing, setPricing] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionTotal, setTransactionTotal] = useState(0);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [billingInterval, setBillingInterval] = useState('month');
  const [checkoutLoading, setCheckoutLoading] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [previewingPriceId, setPreviewingPriceId] = useState('');
  const [confirmingPlanChange, setConfirmingPlanChange] = useState(false);
  const [removingScheduledChange, setRemovingScheduledChange] = useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const [resumingSubscription, setResumingSubscription] = useState(false);
  const [planChangePreview, setPlanChangePreview] = useState(null);
  const [planChangeDialogOpen, setPlanChangeDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [subscriptionActionError, setSubscriptionActionError] = useState('');
  const [returnNotice, setReturnNotice] = useState(null);

  const loadTransactions = useCallback(async ({ append = false, offset = 0 } = {}) => {
    setTransactionLoading(true);
    try {
      const result = await getCreditTransactions({
        limit: PAGE_SIZE,
        offset: append ? offset : 0
      });
      setTransactions((current) => append ? [...current, ...result.transactions] : result.transactions);
      setTransactionTotal(result.total);
      return result;
    } finally {
      setTransactionLoading(false);
    }
  }, []);

  const loadBilling = useCallback(async () => {
    setPageLoading(true);
    setPageError('');
    const [balanceResult, pricingResult, transactionsResult] = await Promise.allSettled([
      refreshBalance({ force: true }),
      getCreditPricing(),
      getCreditTransactions({ limit: PAGE_SIZE, offset: 0 })
    ]);

    if (pricingResult.status === 'fulfilled') setPricing(pricingResult.value);
    if (transactionsResult.status === 'fulfilled') {
      setTransactions(transactionsResult.value.transactions);
      setTransactionTotal(transactionsResult.value.total);
    }

    const failures = [balanceResult, pricingResult, transactionsResult].filter((result) => result.status === 'rejected');
    if (failures.length === 3) {
      setPageError(failures[0].reason?.message || 'Unable to load billing information.');
    } else if (pricingResult.status === 'rejected') {
      setPageError(pricingResult.reason?.message || 'Pricing information is temporarily unavailable.');
    }
    setPageLoading(false);
  }, [refreshBalance]);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  useEffect(() => {
    if (returnStatus === 'cancelled') {
      clearCheckoutContext();
      setReturnNotice({ severity: 'info', message: 'Checkout was cancelled. Your existing credits and subscription were not changed.' });
      const next = new URLSearchParams(searchParams);
      next.delete('credits');
      setSearchParams(next, { replace: true });
      return undefined;
    }

    if (returnStatus !== 'success') return undefined;

    let alive = true;
    let timerId;
    const startedAt = Date.now();
    const snapshot = readCheckoutContext();
    setReturnNotice({ severity: 'info', processing: true, message: 'Stripe confirmed your checkout. We are waiting for the credit update to finish.' });

    const poll = async () => {
      const [nextBalanceResult, nextPricingResult] = await Promise.allSettled([
        refreshBalance({ force: true, silent: true }),
        getCreditPricing()
      ]);
      if (!alive) return;

      const nextBalance = nextBalanceResult.status === 'fulfilled' ? nextBalanceResult.value : null;
      const nextPricing = nextPricingResult.status === 'fulfilled' ? nextPricingResult.value : null;
      if (nextPricing) setPricing(nextPricing);

      if (checkoutUpdated(snapshot, nextBalance, nextPricing)) {
        clearCheckoutContext();
        setReturnNotice({ severity: 'success', message: 'Your billing and credit information is now up to date.' });
        loadTransactions().catch(() => {});
        const next = new URLSearchParams(searchParams);
        next.delete('credits');
        setSearchParams(next, { replace: true });
        return;
      }

      if (Date.now() - startedAt >= 30000) {
        setReturnNotice({ severity: 'warning', message: 'Stripe is still processing the update. You can refresh the billing data manually in a moment.' });
        return;
      }

      timerId = window.setTimeout(poll, 2500);
    };

    poll();
    return () => {
      alive = false;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [loadTransactions, refreshBalance, returnStatus, searchParams, setSearchParams]);

  const plans = useMemo(
    () => (Array.isArray(pricing?.subscription_plans) ? pricing.subscription_plans : []),
    [pricing]
  );
  const packages = useMemo(
    () => (Array.isArray(pricing?.purchase_packages) ? pricing.purchase_packages : []),
    [pricing]
  );
  const currentSubscription = pricing?.current_subscription || null;
  const hasActiveSubscription = Boolean(currentSubscription?.active);
  const currentPlan = plans.find((plan) => plan.price_id === currentSubscription?.price_id) || null;
  const pendingPlan = plans.find((plan) => plan.price_id === currentSubscription?.scheduled_price_id) || null;
  const scheduledCancellation = Boolean(
    currentSubscription?.scheduled_cancellation ?? currentSubscription?.cancel_at_period_end
  );
  const scheduledChangeDate = currentSubscription?.scheduled_change_at || currentSubscription?.period_end;
  const canChangePlan = currentSubscription?.can_change_plan ?? (hasActiveSubscription && !scheduledCancellation);
  const canCancel = currentSubscription?.can_cancel ?? (hasActiveSubscription && !scheduledCancellation);
  const canResume = currentSubscription?.can_resume ?? (hasActiveSubscription && scheduledCancellation);
  const canRemoveScheduledChange = currentSubscription?.can_remove_scheduled_change ?? Boolean(currentSubscription?.scheduled_price_id);
  const subscriptionMutationBusy = Boolean(
    confirmingPlanChange || removingScheduledChange || cancelingSubscription || resumingSubscription
  );
  const availableIntervals = useMemo(
    () => [...new Set(plans.map((plan) => String(plan.interval || 'month').toLowerCase()))],
    [plans]
  );
  const visiblePlans = plans.filter((plan) => String(plan.interval || 'month').toLowerCase() === billingInterval);

  useEffect(() => {
    const currentInterval = String(currentPlan?.interval || '').toLowerCase();
    if (availableIntervals.includes(currentInterval)) {
      setBillingInterval(currentInterval);
    } else if (availableIntervals.length && !availableIntervals.includes(billingInterval)) {
      setBillingInterval(availableIntervals[0]);
    }
  }, [availableIntervals, billingInterval, currentPlan?.interval]);

  const beginSubscriptionCheckout = async (plan) => {
    setCheckoutLoading(`plan:${plan.price_id}`);
    try {
      saveCheckoutContext({
        kind: 'subscription',
        selection_id: plan.price_id,
        available_credits: numberValue(balance?.available_credits),
        purchased_credits: numberValue(balance?.purchased_credits),
        previous_price_id: currentSubscription?.price_id || null
      });
      const result = await createSubscriptionCheckout(plan.price_id);
      if (!result?.checkout_url) throw new Error('Checkout URL was not returned.');
      window.location.assign(result.checkout_url);
    } catch (error) {
      clearCheckoutContext();
      setPageError(error?.message || 'Unable to start subscription checkout.');
      setCheckoutLoading('');
    }
  };

  const reconcileSubscriptionState = async (result, successMessage) => {
    if (Object.prototype.hasOwnProperty.call(result || {}, 'current_subscription')) {
      const nextSubscription = result.current_subscription;
      setPricing((current) => ({
        ...(current || {}),
        current_subscription: nextSubscription,
        subscription_plans: Array.isArray(current?.subscription_plans)
          ? current.subscription_plans.map((plan) => ({
              ...plan,
              is_current: Boolean(nextSubscription?.active && plan.price_id === nextSubscription?.price_id)
            }))
          : []
      }));
    }

    setSubscriptionActionError('');
    setReturnNotice({ severity: 'success', message: successMessage || result?.message || 'Subscription updated.' });

    const [pricingResult] = await Promise.allSettled([
      getCreditPricing(),
      refreshBalance({ force: true, silent: true })
    ]);

    if (pricingResult.status === 'fulfilled') {
      setPricing(pricingResult.value);
    } else {
      setPageError('The subscription change was saved, but the latest billing details could not be refreshed. Use Refresh billing to try again.');
    }
  };

  const handlePreviewPlanChange = async (plan) => {
    setPreviewingPriceId(plan.price_id);
    setSubscriptionActionError('');
    try {
      const preview = await previewSubscriptionChange(plan.price_id);
      setPlanChangePreview(preview);
      setPlanChangeDialogOpen(true);
    } catch (error) {
      setSubscriptionActionError(billingActionErrorMessage(error, 'Unable to preview this plan change.'));
    } finally {
      setPreviewingPriceId('');
    }
  };

  const handleConfirmPlanChange = async () => {
    const targetPlan = planChangePreview?.target_plan;
    if (!targetPlan?.price_id) return;

    setConfirmingPlanChange(true);
    setSubscriptionActionError('');
    try {
      const result = await scheduleSubscriptionChange(targetPlan.price_id);
      const nextSubscription = result?.current_subscription;
      const effectiveAt = nextSubscription?.scheduled_change_at || planChangePreview?.effective_at;
      await reconcileSubscriptionState(
        result,
        `Your plan will change to ${targetPlan.name || 'the selected plan'} on ${formatDate(effectiveAt)}.`
      );
      setPlanChangeDialogOpen(false);
      setPlanChangePreview(null);
    } catch (error) {
      setSubscriptionActionError(billingActionErrorMessage(error, 'Unable to schedule the plan change.'));
    } finally {
      setConfirmingPlanChange(false);
    }
  };

  const handleRemoveScheduledChange = async () => {
    setRemovingScheduledChange(true);
    setSubscriptionActionError('');
    try {
      const result = await removeScheduledSubscriptionChange();
      await reconcileSubscriptionState(result, result?.message || 'The scheduled plan change was removed.');
    } catch (error) {
      setSubscriptionActionError(billingActionErrorMessage(error, 'Unable to remove the scheduled plan change.'));
    } finally {
      setRemovingScheduledChange(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelingSubscription(true);
    setSubscriptionActionError('');
    try {
      const result = await cancelSubscription();
      const cancellationDate = result?.current_subscription?.period_end || currentSubscription?.period_end;
      await reconcileSubscriptionState(
        result,
        `Your subscription will remain active until ${formatDate(cancellationDate)}.`
      );
      setCancelDialogOpen(false);
    } catch (error) {
      setSubscriptionActionError(billingActionErrorMessage(error, 'Unable to cancel the subscription.'));
    } finally {
      setCancelingSubscription(false);
    }
  };

  const handleResumeSubscription = async () => {
    setResumingSubscription(true);
    setSubscriptionActionError('');
    try {
      const result = await resumeSubscription();
      await reconcileSubscriptionState(result, result?.message || 'Your subscription will continue renewing.');
    } catch (error) {
      setSubscriptionActionError(billingActionErrorMessage(error, 'Unable to keep the subscription active.'));
    } finally {
      setResumingSubscription(false);
    }
  };

  const beginPackageCheckout = async (creditPackage) => {
    const packageId = creditPackage.package_id || creditPackage.id;
    setCheckoutLoading(`package:${packageId}`);
    try {
      saveCheckoutContext({
        kind: 'purchase',
        selection_id: packageId,
        available_credits: numberValue(balance?.available_credits),
        purchased_credits: numberValue(balance?.purchased_credits)
      });
      const result = await createCreditPurchaseCheckout(packageId);
      if (!result?.checkout_url) throw new Error('Checkout URL was not returned.');
      window.location.assign(result.checkout_url);
    } catch (error) {
      clearCheckoutContext();
      setPageError(error?.message || 'Unable to start credit checkout.');
      setCheckoutLoading('');
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    setSubscriptionActionError('');
    try {
      const result = await createSubscriptionPortal();
      if (!result?.portal_url) throw new Error('Customer portal URL was not returned.');
      window.location.assign(result.portal_url);
    } catch (error) {
      setSubscriptionActionError(billingActionErrorMessage(error, 'Unable to open payment methods and invoices.'));
      setPortalLoading(false);
    }
  };

  const showInitialSkeleton = pageLoading && !pricing && !balance;

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 74px)',
        p: { xs: 2, md: 3.5 },
        bgcolor: '#F5F9FE',
        backgroundImage: `radial-gradient(circle at 8% 0%, ${alpha(BRAND, 0.1)}, transparent 28%), radial-gradient(${alpha(BRAND, 0.08)} 1px, transparent 1px)`,
        backgroundSize: 'auto, 20px 20px'
      }}
    >
      <Box sx={{ maxWidth: 1380, mx: 'auto' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 2.5 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, color: '#0B203A' }}>Billing & Credits</Typography>
            <Typography variant="body2" sx={{ mt: 0.55, color: '#65758B', maxWidth: 680, lineHeight: 1.65 }}>
              See your available testing credits, subscription, and billing activity in one place.
            </Typography>
          </Box>
          <Button size="small" variant="outlined" startIcon={<AutorenewRoundedIcon />} onClick={loadBilling} disabled={pageLoading} sx={{ borderRadius: 2.5, fontWeight: 800, textTransform: 'none' }}>
            Refresh
          </Button>
        </Stack>

        {returnNotice ? (
          <Alert
            severity={returnNotice.severity}
            icon={returnNotice.processing ? <CircularProgress size={20} /> : undefined}
            action={returnNotice.severity === 'warning' ? <Button color="inherit" size="small" onClick={loadBilling}>Refresh</Button> : null}
            sx={{ mb: 2.5, borderRadius: 3 }}
          >
            {returnNotice.message}
          </Alert>
        ) : null}
        {pageError || balanceError ? <Alert severity="error" sx={{ mb: 2.5, borderRadius: 3 }}>{pageError || balanceError}</Alert> : null}
        {subscriptionActionError ? (
          <Alert severity="error" onClose={() => setSubscriptionActionError('')} sx={{ mb: 2.5, borderRadius: 3 }}>
            {subscriptionActionError}
          </Alert>
        ) : null}

        {showInitialSkeleton ? <Skeleton variant="rounded" height={260} sx={{ borderRadius: 4, mb: 2.5 }} /> : (
          <Card sx={{ ...surfaceSx, mb: 2.5, overflow: 'hidden' }}>
            {balanceLoading ? <LinearProgress /> : null}
            <Grid container alignItems="stretch">
              <Grid item xs={12} lg={5} sx={{ bgcolor: alpha(BRAND, 0.025), borderRight: { lg: `1px solid ${alpha(BRAND, 0.11)}` }, borderBottom: { xs: `1px solid ${alpha(BRAND, 0.11)}`, lg: 0 } }}>
                <CardContent sx={{ p: { xs: 2.2, md: 3 }, height: '100%' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 900, letterSpacing: '0.09em', textTransform: 'uppercase', color: BRAND }}>
                        Current Credit Balance
                      </Typography>
                      <Typography sx={{ mt: 0.8, fontSize: { xs: '2.7rem', md: '3.7rem' }, lineHeight: 1, fontWeight: 950, color: '#0B203A' }}>
                        {formatNumber(balance?.available_credits)}
                      </Typography>
                      <Typography sx={{ mt: 0.7, color: '#65758B', fontWeight: 700 }}>credits available for new work</Typography>
                    </Box>
                    <Box sx={{ width: 56, height: 56, borderRadius: 3.5, display: 'grid', placeItems: 'center', color: BRAND, bgcolor: alpha(BRAND, 0.1) }}>
                      <AccountBalanceWalletRoundedIcon fontSize="large" />
                    </Box>
                  </Stack>

                  {numberValue(balance?.available_credits) === 0 ? (
                    <Alert severity="warning" sx={{ mt: 2.5, borderRadius: 3 }}>
                      No credits are currently available. Choose a plan or purchase additional credits below before launching another assessment.
                    </Alert>
                  ) : null}

                  <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                    <Grid item xs={12} sm={4}><Metric label="Subscription" value={formatNumber(balance?.subscription_credits)} helper="Current-period credits" /></Grid>
                    <Grid item xs={12} sm={4}><Metric label="Purchased" value={formatNumber(balance?.purchased_credits)} helper="One-time credits" /></Grid>
                    <Grid item xs={12} sm={4}><Metric label="Reserved" value={formatNumber(balance?.reserved_credits)} helper="Allocated to running work" accent="#D97706" /></Grid>
                  </Grid>
                </CardContent>
              </Grid>

              <Grid item xs={12} lg={7}>
                <CardContent sx={{ p: { xs: 2.2, md: 3 } }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'flex-start' }} spacing={1.2}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 900, letterSpacing: '0.09em', textTransform: 'uppercase', color: BRAND }}>Current Subscription</Typography>
                      <Typography variant="h5" sx={{ mt: 0.8, fontWeight: 900, color: '#0B203A', overflowWrap: 'anywhere' }}>
                        {hasActiveSubscription ? friendlyPlanName(currentPlan || currentSubscription) : 'No active plan'}
                      </Typography>
                    </Box>
                    {hasActiveSubscription ? <Chip size="small" label={currentSubscription.status || 'Unknown'} color={subscriptionStatusColor(currentSubscription.status)} sx={{ fontWeight: 800, textTransform: 'capitalize' }} /> : null}
                  </Stack>

                  {hasActiveSubscription ? (
                    <>
                      <Grid container spacing={1.3} sx={{ mt: 1.4 }}>
                        <Grid item xs={6}><Metric label="Included" value={formatNumber(currentSubscription.included_credits ?? currentSubscription.credits ?? currentPlan?.credits)} /></Grid>
                        <Grid item xs={6}><Metric label="Plan Price" value={currentPlan ? formatCurrency(currentPlan.unit_amount, currentPlan.currency) : 'Unavailable'} helper={currentPlan ? `per ${planInterval(currentPlan)}` : ''} /></Grid>
                      </Grid>
                      <Stack spacing={1.1} sx={{ mt: 2 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={0.4}><Typography variant="body2" color="text.secondary">Current billing period</Typography><Typography variant="body2" fontWeight={750} sx={{ overflowWrap: 'anywhere' }}>{formatDate(currentSubscription.period_start || currentSubscription.current_period_start)} to {formatDate(currentSubscription.period_end || currentSubscription.current_period_end || balance?.credits_period_end)}</Typography></Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={0.4}><Typography variant="body2" color="text.secondary">Renews</Typography><Typography variant="body2" fontWeight={750}>{scheduledCancellation ? 'No, cancellation scheduled' : `Yes, on ${formatDate(currentSubscription.period_end)}`}</Typography></Stack>
                      </Stack>

                      {currentSubscription?.scheduled_price_id ? (
                        <Paper elevation={0} sx={{ mt: 2.2, p: 1.7, borderRadius: 3, border: '1px solid', borderColor: alpha(BRAND, 0.18), bgcolor: alpha(BRAND, 0.055) }}>
                          <Stack direction="row" spacing={1.2} alignItems="flex-start">
                            <EventAvailableRoundedIcon sx={{ mt: 0.15, color: BRAND }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 900, color: '#123454' }}>Plan change scheduled</Typography>
                              <Typography variant="body2" sx={{ mt: 0.35, color: '#52677E', lineHeight: 1.55 }}>
                                {friendlyPlanName(pendingPlan)} with {formatNumber(currentSubscription.scheduled_credits ?? pendingPlan?.credits)} credits begins {formatDate(scheduledChangeDate)}.
                              </Typography>
                            </Box>
                          </Stack>
                          {canRemoveScheduledChange ? (
                            <Button size="small" startIcon={removingScheduledChange ? <CircularProgress size={15} /> : <RestartAltRoundedIcon />} onClick={handleRemoveScheduledChange} disabled={removingScheduledChange || subscriptionMutationBusy} sx={{ mt: 1, ml: { xs: 0, sm: 3.8 }, fontWeight: 850, textTransform: 'none', whiteSpace: 'normal', textAlign: 'left' }}>
                              {removingScheduledChange ? 'Removing...' : 'Undo scheduled change'}
                            </Button>
                          ) : null}
                        </Paper>
                      ) : null}

                      {scheduledCancellation ? (
                        <Alert severity="warning" sx={{ mt: 2.2, borderRadius: 3 }}>
                          Your subscription will end on {formatDate(currentSubscription.period_end)}. Credits and access remain available until then.
                        </Alert>
                      ) : null}

                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.1} sx={{ mt: 2.3 }}>
                        <Button fullWidth variant="outlined" startIcon={portalLoading ? <CircularProgress size={16} /> : <OpenInNewRoundedIcon />} onClick={openPortal} disabled={portalLoading} sx={{ borderRadius: 3, fontWeight: 850, textTransform: 'none' }}>
                          {portalLoading ? 'Opening...' : 'Payment methods & invoices'}
                        </Button>
                        {scheduledCancellation && canResume ? (
                          <Button fullWidth variant="contained" startIcon={resumingSubscription ? <CircularProgress size={16} color="inherit" /> : <AutorenewRoundedIcon />} onClick={handleResumeSubscription} disabled={resumingSubscription || subscriptionMutationBusy} sx={{ borderRadius: 3, fontWeight: 850, textTransform: 'none' }}>
                            {resumingSubscription ? 'Keeping...' : 'Keep subscription'}
                          </Button>
                        ) : canCancel ? (
                          <Button fullWidth variant="outlined" color="error" startIcon={<CancelOutlinedIcon />} onClick={() => { setSubscriptionActionError(''); setCancelDialogOpen(true); }} disabled={subscriptionMutationBusy} sx={{ borderRadius: 3, fontWeight: 850, textTransform: 'none' }}>
                            Cancel subscription
                          </Button>
                        ) : null}
                      </Stack>
                    </>
                  ) : (
                    <Box sx={{ mt: 2, p: 2.2, borderRadius: 3, bgcolor: alpha(BRAND, 0.055), border: '1px dashed', borderColor: alpha(BRAND, 0.2) }}>
                      <Typography sx={{ fontWeight: 800, color: '#183554' }}>Choose a plan below</Typography>
                      <Typography variant="body2" sx={{ mt: 0.6, color: '#65758B', lineHeight: 1.65 }}>Subscription credits renew with each billing period and provide predictable testing capacity.</Typography>
                    </Box>
                  )}
                </CardContent>
              </Grid>
            </Grid>
          </Card>
        )}

        <Card sx={{ ...surfaceSx, mb: 2.5 }}>
          <CardContent sx={{ p: { xs: 2.2, md: 3 } }}>
            <SectionTitle
              eyebrow="Subscription"
              title="Choose your recurring credit allowance"
              description={hasActiveSubscription ? 'Compare plans below. Any change starts at your next renewal, with no immediate charge or proration.' : 'Choose how many testing credits your team should receive each billing period.'}
              action={availableIntervals.length > 1 ? (
                <ToggleButtonGroup exclusive size="small" value={billingInterval} onChange={(_, value) => value && setBillingInterval(value)} sx={{ '& .MuiToggleButton-root': { px: 2, borderRadius: 2.5, textTransform: 'none', fontWeight: 800 } }}>
                  {availableIntervals.map((interval) => <ToggleButton key={interval} value={interval}>{interval === 'year' ? 'Annual' : 'Monthly'}</ToggleButton>)}
                </ToggleButtonGroup>
              ) : null}
            />

            {hasActiveSubscription && !canChangePlan ? (
              <Alert severity={scheduledCancellation ? 'warning' : 'info'} sx={{ mt: 2, borderRadius: 3 }}>
                {scheduledCancellation
                  ? `Plan changes are unavailable because this subscription is scheduled to end on ${formatDate(currentSubscription?.period_end)}. Keep the subscription first if you want to change plans.`
                  : 'Plan changes are not currently available for this subscription.'}
              </Alert>
            ) : null}

            {visiblePlans.length ? (
              <Stack spacing={1.2} sx={{ mt: 2.2 }}>
                {visiblePlans.map((plan) => {
                  const isCurrent = Boolean(hasActiveSubscription && (plan.is_current || plan.price_id === currentSubscription?.price_id));
                  const isScheduled = Boolean(hasActiveSubscription && plan.price_id === currentSubscription?.scheduled_price_id);
                  const purchasable = plan.active !== false && !isCurrent;
                  const currentCredits = numberValue(currentSubscription?.credits ?? currentPlan?.credits);
                  const targetCredits = numberValue(plan.credits);
                  const planChangeLabel = targetCredits > currentCredits
                    ? 'Upgrade'
                    : targetCredits < currentCredits
                      ? 'Downgrade'
                      : 'Change plan';
                  const actionDisabled = Boolean(
                    !purchasable ||
                    checkoutLoading ||
                    subscriptionMutationBusy ||
                    previewingPriceId ||
                    (hasActiveSubscription && (!canChangePlan || isScheduled))
                  );
                  return (
                    <Paper key={plan.price_id} elevation={0} sx={{ p: { xs: 1.8, md: 2 }, borderRadius: 3.25, border: '1px solid', borderColor: isCurrent || isScheduled ? alpha(BRAND, 0.42) : alpha(BRAND, 0.12), bgcolor: isCurrent ? alpha(BRAND, 0.045) : '#FFFFFF' }}>
                      <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="center">
                        <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="h6" sx={{ fontWeight: 900, color: '#10243E', overflowWrap: 'anywhere' }}>{friendlyPlanName(plan)}</Typography>
                            {isCurrent ? <Chip size="small" icon={<CheckCircleRoundedIcon />} label="Current" color="primary" sx={{ fontWeight: 800 }} /> : isScheduled ? <Chip size="small" icon={<EventAvailableRoundedIcon />} label="Scheduled" color="primary" variant="outlined" sx={{ fontWeight: 800 }} /> : null}
                          </Stack>
                          <Typography variant="body2" sx={{ mt: 0.35, color: '#65758B' }}>Renews every {planInterval(plan)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                          <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#718197' }}>Credits</Typography>
                          <Typography sx={{ mt: 0.25, fontWeight: 900, color: '#183554' }}>{formatNumber(plan.credits)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                          <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#718197' }}>Price</Typography>
                          <Typography sx={{ mt: 0.25, fontWeight: 900, color: '#183554' }}>{formatCurrency(plan.unit_amount, plan.currency)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4} md={4}>
                          <Button fullWidth variant={isCurrent || isScheduled ? 'outlined' : 'contained'} startIcon={previewingPriceId === plan.price_id ? <CircularProgress size={16} color="inherit" /> : hasActiveSubscription && !isCurrent ? <CompareArrowsRoundedIcon /> : undefined} disabled={actionDisabled} onClick={() => hasActiveSubscription ? handlePreviewPlanChange(plan) : beginSubscriptionCheckout(plan)} sx={{ minHeight: 42, borderRadius: 2.5, fontWeight: 850, textTransform: 'none', whiteSpace: 'normal', lineHeight: 1.25 }}>
                            {isCurrent ? 'Current plan' : isScheduled ? 'Scheduled for renewal' : previewingPriceId === plan.price_id ? 'Preparing preview...' : checkoutLoading === `plan:${plan.price_id}` ? 'Opening checkout...' : plan.active === false ? 'Unavailable' : hasActiveSubscription ? planChangeLabel : 'Choose this plan'}
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  );
                })}
              </Stack>
            ) : <EmptyState title="No plans in this billing interval" description="Subscription plans will appear here when they are available." />}
          </CardContent>
        </Card>

        <Card sx={{ ...surfaceSx, mb: 2.5 }}>
          <CardContent sx={{ p: { xs: 2.2, md: 3 } }}>
            <SectionTitle
              eyebrow="Credit Packs"
              title="Need additional credits?"
              description="Purchase one-time credits for temporary extra capacity. These credits do not renew and your subscription stays exactly the same."
            />

            {packages.length ? (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {packages.map((creditPackage) => {
                  const packageId = creditPackage.package_id || creditPackage.id;
                  const hasPrice = creditPackage.unit_amount !== null && creditPackage.unit_amount !== undefined && creditPackage.currency;
                  return (
                    <Grid item xs={12} sm={6} lg={3} key={packageId}>
                      <Paper elevation={0} sx={{ p: 2.2, height: '100%', borderRadius: 3.5, border: '1px solid', borderColor: alpha(BRAND, 0.13) }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                          <CreditCardRoundedIcon sx={{ color: BRAND }} />
                          <Chip size="small" label="One-time" variant="outlined" sx={{ fontWeight: 800 }} />
                        </Stack>
                        <Typography variant="h6" sx={{ mt: 1, fontWeight: 900, color: '#10243E', overflowWrap: 'anywhere' }}>{creditPackage.label || creditPackage.name || 'Credit pack'}</Typography>
                        <Typography sx={{ mt: 0.4, color: '#65758B', fontWeight: 700 }}>{formatNumber(creditPackage.credits ?? creditPackage.credit_quantity)} additional credits</Typography>
                        <Typography variant="h5" sx={{ mt: 1.5, fontWeight: 950, color: '#0B203A' }}>{formatCurrency(creditPackage.unit_amount, creditPackage.currency)}</Typography>
                        <Tooltip title={hasPrice ? '' : 'The backend has not configured a Stripe price for this package yet.'}>
                          <span>
                            <Button fullWidth variant="outlined" disabled={!hasPrice || Boolean(checkoutLoading)} onClick={() => beginPackageCheckout(creditPackage)} sx={{ mt: 1.8, borderRadius: 3, fontWeight: 850 }}>
                              {checkoutLoading === `package:${packageId}` ? 'Opening checkout...' : 'Buy credit pack'}
                            </Button>
                          </span>
                        </Tooltip>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Paper elevation={0} sx={{ mt: 2, p: 1.8, borderRadius: 3, border: '1px dashed', borderColor: alpha(BRAND, 0.2), bgcolor: alpha(BRAND, 0.025) }}>
                <Stack direction="row" spacing={1.4} alignItems="center">
                  <Box sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: 2.5, display: 'grid', placeItems: 'center', color: BRAND, bgcolor: alpha(BRAND, 0.09) }}><AddCardRoundedIcon /></Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 850, color: '#183554' }}>No credit packs are available right now</Typography>
                    <Typography variant="body2" sx={{ mt: 0.2, color: '#65758B', lineHeight: 1.55 }}>Your recurring subscription credits are unaffected.</Typography>
                  </Box>
                </Stack>
              </Paper>
            )}
          </CardContent>
        </Card>

        <Card sx={{ ...surfaceSx, overflow: 'hidden' }}>
          <CardContent sx={{ p: { xs: 2.2, md: 3 }, pb: 2 }}>
            <SectionTitle
              eyebrow="Billing History"
              title="Credit activity"
              description="Review credits added, reserved, charged, refunded, or expired for this team."
              action={<Chip icon={<HistoryRoundedIcon />} label={`${transactionTotal || transactions.length} entries`} variant="outlined" sx={{ fontWeight: 800 }} />}
            />
          </CardContent>
          <Divider />
          <TableContainer sx={{ maxHeight: 560 }}>
            <Table stickyHeader size="small" sx={{ minWidth: 920 }}>
              <TableHead><TableRow sx={{ '& th': { bgcolor: '#EDF5FF', color: '#24415F', fontWeight: 900 } }}><TableCell>Date</TableCell><TableCell>Description</TableCell><TableCell align="right">Credits</TableCell><TableCell>Balances</TableCell><TableCell>Pentest Type</TableCell><TableCell>Usage</TableCell></TableRow></TableHead>
              <TableBody>
                {transactions.length ? transactions.map((transaction, index) => {
                  const amount = transactionAmount(transaction);
                  return (
                    <TableRow key={transaction.id || `${transaction.created_at || 'transaction'}-${index}`} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: '#52677E' }}>{formatDate(transaction.created_at || transaction.date, true)}</TableCell>
                      <TableCell><Typography sx={{ fontWeight: 750, color: '#183554' }}>{transaction.description || transaction.type || 'Credit transaction'}</Typography><Typography variant="caption" color="text.secondary">{transaction.source || transaction.balance_type || ''}</Typography></TableCell>
                      <TableCell align="right"><Chip size="small" label={`${amount > 0 ? '+' : ''}${formatNumber(amount)}`} color={amount > 0 ? 'success' : amount < 0 ? 'warning' : 'default'} sx={{ fontWeight: 900 }} /></TableCell>
                      <TableCell><Typography variant="body2" sx={{ color: '#334E68' }}>Subscription: {formatNumber(transaction.subscription_balance ?? transaction.subscription_credits)}</Typography><Typography variant="caption" color="text.secondary">Purchased: {formatNumber(transaction.purchased_balance ?? transaction.purchased_credits)}</Typography></TableCell>
                      <TableCell>{transaction.pentest_type ? <Chip size="small" label={transaction.pentest_type} variant="outlined" /> : '-'}</TableCell>
                      <TableCell><Typography variant="body2" sx={{ color: '#334E68' }}>{transaction.units !== undefined ? `${formatNumber(transaction.units)} units` : '-'}</Typography><Typography variant="caption" color="text.secondary">{transaction.unit_cost !== undefined ? `${formatNumber(transaction.unit_cost)} credits/unit` : ''}</Typography></TableCell>
                    </TableRow>
                  );
                }) : <TableRow><TableCell colSpan={6}><EmptyState title="No credit transactions yet" description="Grants, purchases, reservations, charges, refunds, and expirations will be recorded here." /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
          {transactions.length > 0 && (transactionTotal > transactions.length || transactions.length % PAGE_SIZE === 0) ? (
            <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid', borderColor: alpha(BRAND, 0.1) }}><Button variant="outlined" onClick={() => loadTransactions({ append: true, offset: transactions.length }).catch((error) => setPageError(error.message))} disabled={transactionLoading} sx={{ borderRadius: 3, fontWeight: 800 }}>{transactionLoading ? 'Loading...' : 'Load more transactions'}</Button></Box>
          ) : null}
        </Card>
      </Box>

      <Dialog
        open={planChangeDialogOpen}
        onClose={() => {
          if (confirmingPlanChange) return;
          setPlanChangeDialogOpen(false);
          setPlanChangePreview(null);
          setSubscriptionActionError('');
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ px: { xs: 2.2, md: 3 }, pt: 3, pb: 1 }}>
          <Stack direction="row" spacing={1.3} alignItems="center">
            <Box sx={{ width: 44, height: 44, borderRadius: 3, display: 'grid', placeItems: 'center', color: BRAND, bgcolor: alpha(BRAND, 0.1) }}>
              <CompareArrowsRoundedIcon />
            </Box>
            <Box>
              <Typography sx={{ color: BRAND, fontSize: '0.7rem', fontWeight: 900, letterSpacing: '0.09em', textTransform: 'uppercase' }}>Plan Change Preview</Typography>
              <Typography variant="h5" sx={{ mt: 0.25, fontWeight: 900, color: '#0B203A' }}>Review your next renewal</Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2.2, md: 3 }, pt: '18px !important' }}>
          {subscriptionActionError ? <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>{subscriptionActionError}</Alert> : null}
          <Grid container spacing={2}>
            {[
              { label: 'Current plan', plan: planChangePreview?.current_plan },
              { label: 'New plan', plan: planChangePreview?.target_plan, accent: true }
            ].map(({ label, plan, accent }) => (
              <Grid item xs={12} md={6} key={label}>
                <Paper elevation={0} sx={{ p: 2.2, height: '100%', minWidth: 0, borderRadius: 3.5, border: '1px solid', borderColor: accent ? BRAND : alpha(BRAND, 0.14), bgcolor: accent ? alpha(BRAND, 0.045) : '#FAFCFF' }}>
                  <Typography sx={{ color: accent ? BRAND : '#65758B', fontSize: '0.7rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</Typography>
                  <Typography variant="h6" sx={{ mt: 0.7, fontWeight: 900, color: '#10243E', overflowWrap: 'anywhere' }}>{friendlyPlanName(plan)}</Typography>
                  <Typography sx={{ mt: 1.2, fontWeight: 850, color: '#183554' }}>{formatNumber(plan?.credits)} included credits</Typography>
                  <Typography variant="h5" sx={{ mt: 1.1, fontWeight: 950, color: '#0B203A' }}>{formatCurrency(plan?.unit_amount, plan?.currency)}</Typography>
                  <Typography variant="body2" sx={{ mt: 0.3, color: '#65758B' }}>per {planInterval(plan)}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Paper elevation={0} sx={{ mt: 2, p: 2.2, borderRadius: 3.5, border: '1px solid', borderColor: alpha('#15803D', 0.18), bgcolor: alpha('#15803D', 0.055) }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <EventAvailableRoundedIcon sx={{ color: '#15803D' }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 900, color: '#14532D' }}>Effective {formatDate(planChangePreview?.effective_at)}</Typography>
                <Typography variant="body2" sx={{ mt: 0.3, color: '#3F6250' }}>Your current plan remains unchanged until the next renewal.</Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label="No charge today" color="success" variant="outlined" sx={{ fontWeight: 800 }} />
                <Chip label="No proration" color="success" variant="outlined" sx={{ fontWeight: 800 }} />
              </Stack>
            </Stack>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2.2, md: 3 }, pb: 3, pt: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => { setPlanChangeDialogOpen(false); setPlanChangePreview(null); setSubscriptionActionError(''); }} disabled={confirmingPlanChange} sx={{ borderRadius: 3, fontWeight: 850, textTransform: 'none' }}>Not now</Button>
          <Button variant="contained" startIcon={confirmingPlanChange ? <CircularProgress size={16} color="inherit" /> : <CheckCircleRoundedIcon />} onClick={handleConfirmPlanChange} disabled={confirmingPlanChange} sx={{ borderRadius: 3, px: 2.5, fontWeight: 850, textTransform: 'none' }}>
            {confirmingPlanChange ? 'Scheduling...' : 'Confirm plan change'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelDialogOpen}
        onClose={() => {
          if (cancelingSubscription) return;
          setCancelDialogOpen(false);
          setSubscriptionActionError('');
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ px: { xs: 2.2, md: 3 }, pt: 3, pb: 1 }}>
          <Typography sx={{ color: '#B42318', fontSize: '0.7rem', fontWeight: 900, letterSpacing: '0.09em', textTransform: 'uppercase' }}>Subscription Cancellation</Typography>
          <Typography variant="h5" sx={{ mt: 0.45, fontWeight: 900, color: '#0B203A' }}>Are you sure you want to cancel?</Typography>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2.2, md: 3 }, pt: '14px !important' }}>
          {subscriptionActionError ? <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>{subscriptionActionError}</Alert> : null}
          <Typography sx={{ color: '#52677E', lineHeight: 1.75 }}>
            Your subscription, platform access, and remaining credits will stay available through <strong>{formatDate(currentSubscription?.period_end)}</strong>. The subscription will not renew after that date.
          </Typography>
          {currentSubscription?.scheduled_price_id ? (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: 3 }}>
              Canceling also removes the pending change to {friendlyPlanName(pendingPlan)}.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2.2, md: 3 }, pb: 3, pt: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => { setCancelDialogOpen(false); setSubscriptionActionError(''); }} disabled={cancelingSubscription} sx={{ borderRadius: 3, fontWeight: 850, textTransform: 'none' }}>No, keep subscription</Button>
          <Button variant="contained" color="error" startIcon={cancelingSubscription ? <CircularProgress size={16} color="inherit" /> : <CancelOutlinedIcon />} onClick={handleCancelSubscription} disabled={cancelingSubscription} sx={{ borderRadius: 3, px: 2.5, fontWeight: 850, textTransform: 'none' }}>
            {cancelingSubscription ? 'Scheduling cancellation...' : 'Yes, cancel at period end'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingPage;
